import { normalisePhone, type AllergySeverity, type CreateChildRequest } from '@daghfal/core';

/** What the allergy and contact editors hold, and how it becomes a request (Q9). */
export interface AllergyDraft { allergen: string; severity: AllergySeverity; reaction: string; actionRequired: string }
export interface ContactDraft { name: string; relationship: string; phone: string }

type AllergyRequest = NonNullable<CreateChildRequest['allergies']>[number];
type ContactRequest = NonNullable<CreateChildRequest['emergencyContacts']>[number];

export const emptyAllergy = (): AllergyDraft => ({ allergen: '', severity: 'Mild', reaction: '', actionRequired: '' });
export const emptyContact = (): ContactDraft => ({ name: '', relationship: '', phone: '' });

/**
 * The drafts as the API takes them. Only a row left entirely blank is dropped: a half-filled one is sent,
 * so the API can say what it lacks rather than the row vanishing with what was typed in it.
 */
export function allergiesToRequest(drafts: AllergyDraft[]): AllergyRequest[] {
  return drafts
    .filter((allergy) => allergy.allergen.trim() || allergy.reaction.trim() || allergy.actionRequired.trim())
    .map((allergy) => ({
      allergen: allergy.allergen.trim(),
      severity: allergy.severity,
      reaction: allergy.reaction.trim() || null,
      actionRequired: allergy.actionRequired.trim() || null,
    }));
}

/**
 * The drafts as the API takes them, numbered in the order typed: the order they should be rung. As with
 * allergies, only an entirely blank row is dropped.
 */
export function contactsToRequest(drafts: ContactDraft[]): ContactRequest[] {
  return drafts
    .filter((contact) => contact.name.trim() || contact.phone.trim() || contact.relationship.trim())
    .map((contact, index) => ({
      name: contact.name.trim(),
      relationship: contact.relationship.trim() || null,
      phone: normalisePhone(contact.phone) ?? contact.phone.trim(),
      priority: index + 1,
    }));
}
